import { useEffect, useRef, useState } from "react";
import { Fetch, hrLenseBaseUrl } from "../../shared/Fetch";
import { useNotification } from "../../shared/NotificatonContext";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ITagReq, IUser, IUserStats } from "./modal";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";

const themeColors = {
    red: {
        gradient: "linear-gradient(135deg, #d32f2f, #ff6a6a)",
        dark: "#d32f2f",
        text: "#ffffff"
    },
    green: {
        gradient: "linear-gradient(135deg, #15803d, #4ade80)",
        dark: "#15803d",
        text: "#ffffff"
    },
    blue: {
        gradient: "linear-gradient(135deg, #1e3a8a, #60a5fa)",
        dark: "#1e3a8a",
        text: "#ffffff"
    },
    yellow: {
        gradient: "linear-gradient(135deg, #f59e0b, #fde047)",
        dark: "#e18a05",
        text: "#000000"
    },
    gray: {
        gradient: "linear-gradient(135deg, #4b5563, #d1d5db)",
        dark: "#4b5563",
        text: "#ffffff"
    }
};

export const getSuccessRate = (tried: number, success: number) => {
    if (success < 1) return "0.000";
    const result = success * 100 / tried;
    return result.toFixed(3);
};

export const getDialogTheme = (user: IUser, stats?: IUserStats) => {
    if (!stats) return themeColors.gray;
    let rate = 0;

    if (user.role === 'Sales Manager') {
        rate = stats.po * 100 / stats.requirement;
        if (rate >= 2.5) return themeColors.red;
        if (rate >= 2.0) return themeColors.green;
        if (rate >= 1.5) return themeColors.blue;
        if (rate >= 0.75) return themeColors.yellow;
        return themeColors.gray;
    }
    if (user.role === 'Recruiter') {
        rate = stats.po * 100 / stats.submission;
        if (rate >= 1.5) return themeColors.red;
        if (rate >= 1.0) return themeColors.green;
        if (rate >= 0.75) return themeColors.blue;
        if (rate >= 0.325) return themeColors.yellow;
        return themeColors.gray;
    }
    return themeColors.gray;
};

export function User({ req, show: showPrivateInfo }: { req: ITagReq, show?: boolean }) {
    const toast = useRef<any>();
    const userId = req.userId;
    const userName = req.userName;
    const { getUserById, isOnline, onUserSelect, setReq } = useNotification();
    const user = getUserById(userId);
    const [stats, setStats] = useState<IUserStats>();
    const [loading, setLoading] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [userModalData, setUserModalData] = useState({ data: null as { employeeId?: number, empCode?: string, phoneNumber?: string, whatsAppNumber?: string, personalEmail?: string } | null });

    const getEmployeeDetails = async (empCode: string) => {
        const response = await fetch(`${hrLenseBaseUrl}/Tds_Slab/getempbycode?empCode=${empCode}`, { method: "GET" });
        if (response) {
            const data: { employeeId?: number, empCode?: string, phoneNumber?: string, whatsAppNumber?: string, personalEmail?: string } = await response.json();
            setUserModalData({ data });
        }
    }

    const getUserStats = async (loginId: number) => {
        if (loading) return;
        setLoading(true);
        const response = await Fetch(`Experiment/jv-user-stats`, 'GET', null, `loginId=${loginId}`, toast);
        if (response) {
            const data = await response.json();
            setStats(data);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (user && user.employeeId && showPrivateInfo && showInfo) {
            getEmployeeDetails(user.employeeId);
        }
        if (showInfo && user) {
            getUserStats(user.id);
        }
    }, [user, showPrivateInfo, showInfo]);

    if (!user) return <span>{userName.split('-')[0]}</span>

    const themeColor = getDialogTheme(user, stats);

    return (
        <>
            <div style={styles.container}>
                <div style={styles.avatar} onClick={() => { setShowInfo(true); setUserModalData({ data: null }); }}>
                    <img src={user.employeeId ?
                        `${hrLenseBaseUrl}/Hrlense_Employee/getEmployeeImages?EmployeeCode=${user.employeeId}`
                        : '/img/user-icon.png'}
                        style={styles.avatarImg}
                        onError={e => e.currentTarget.src = '/img/user-icon.png'}
                        loading="lazy" />
                    {isOnline(userId) && <span style={styles.onlineDot}></span>}
                </div>
                <div style={styles.name}>{user.name}</div>
                <Button onClick={() => {
                    setReq(req);
                    onUserSelect(user);
                }} icon="pi pi-tag" className="p-button-sm p-button-text p-0" aria-label="Query" />
            </div>


            <Dialog
                closable visible={showInfo}
                headerStyle={{ background: themeColor.gradient, color: themeColor.text }}
                header={user.name} className="p-fluid"
                onHide={() => { setShowInfo(false); setUserModalData({ data: null }); }}>
                <div style={{
                    display: "flex",
                    gap: 8, paddingTop: 16,
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        position: 'relative',
                    }}>
                        <img src={user.employeeId ?
                            `${hrLenseBaseUrl}/Hrlense_Employee/getEmployeeImages?EmployeeCode=${user.employeeId}`
                            : '/img/user-icon.png'}
                            onError={e => e.currentTarget.src = '/img/user-icon.png'}
                            style={{ width: '320px', height: '320px', objectFit: 'contain', borderRadius: 6 }} loading="lazy" />
                        <Tag style={{ background: themeColor.gradient, color: themeColor.text, position: 'absolute', top: 0, left: 0 }} value={user.role}></Tag>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', marginTop: 8 }}>

                        <div >
                            <div style={styles.info}><b style={styles.infoHead}>Professional Email :</b> <span style={styles.infoBody}>{user.email}</span></div>
                            <div style={styles.info}><b style={styles.infoHead}>Company (Branch) :</b> <span style={styles.infoBody}>{user.companyName} ({user.branchName})</span></div>
                            {showPrivateInfo && <>
                                <div style={styles.info}><b style={styles.infoHead}>Email/Hangout :</b> <span style={styles.infoBody}>{userModalData.data?.personalEmail}</span></div>
                                <div style={styles.info}><b style={styles.infoHead}>Phone :</b> <span style={styles.infoBody}>{userModalData.data?.phoneNumber}</span></div>
                                <div style={styles.info}><b style={styles.infoHead}>WhatsApp :</b> <span style={styles.infoBody}>{userModalData.data?.whatsAppNumber}</span></div>
                            </>}
                        </div>

                        {loading ?
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <i className="pi pi-spin pi-spinner"></i>
                            </div> :
                            <>{(user.role === 'Sales Manager' || user.role === 'Recruiter') &&
                                <div>
                                    {user.role === 'Sales Manager' && <div style={styles.info}><b style={styles.infoHead}>Requirement Success Rate (RSR) :</b> <span style={{ color: themeColor.dark, fontWeight: 800 }}>{stats ? getSuccessRate(stats.requirement, stats.po) : ''}</span></div>}
                                    {user.role === 'Recruiter' && <div style={styles.info}><b style={styles.infoHead}>Submission Success Rate (SSR) :</b> <span style={{ color: themeColor.dark, fontWeight: 800 }}>{stats ? getSuccessRate(stats.submission, stats.po) : ''}</span></div>}
                                    {user.role === 'Sales Manager' && <div style={styles.info}><b style={styles.infoHead}>Total Requirements :</b> <span style={styles.infoBody}>{stats?.requirement}</span></div>}
                                    {user.role === 'Recruiter' && <div style={styles.info}><b style={styles.infoHead}>Total Submissions :</b> <span style={styles.infoBody}>{stats?.submission}</span></div>}
                                    <div style={styles.info}><b style={styles.infoHead}>Total End Client Submissions (ECS) :</b> <span style={styles.infoBody}>{stats?.ecs}</span></div>
                                    <div style={styles.info}><b style={styles.infoHead}>Total End Client Interviews (ECI) :</b> <span style={styles.infoBody}>{stats?.eci}</span></div>
                                    <div style={styles.info}><b style={styles.infoHead}>Total Purchase Orders (PO) :</b> <span style={styles.infoBody}>{stats?.po}</span></div>
                                </div>}</>
                        }

                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, }}>
                            <Button onClick={() => {
                                setReq(req);
                                onUserSelect(user);
                                setShowInfo(false);
                                setUserModalData({ data: null });
                            }} icon="pi pi-send" className="p-button-sm p-button-rounded" label="Message" />
                            <Button onClick={() => {
                                setShowInfo(false);
                                setUserModalData({ data: null });
                            }} className="p-button-sm p-button-secondary p-button-rounded" label="Close" />
                        </div>
                    </div>
                </div>
            </Dialog>
            <Toast ref={toast} />
        </>
    );
}


const styles: Record<string, React.CSSProperties> = {
    container: {
        display: "flex",
        alignItems: "center",
        gap: 5,
    },
    avatar: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 400,
        position: 'relative',
        cursor: 'pointer',
    },
    avatarImg: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        objectFit: "cover",
    },
    name: {
        fontSize: 12,
        fontWeight: 400
    },
    onlineDot: {
        position: "absolute",
        bottom: 1,
        right: 1,
        width: 7,
        height: 7,
        borderRadius: "50%",
        backgroundColor: "#22c55e",
        border: "1px solid white"
    },
    info: {
        fontWeight: 600,
        fontSize: 14,
        display: 'flex',
        justifyContent: 'space-between',
        padding: 2,
    },
    infoHead: {
        color: '#787878',
    },
    infoBody: {
        color: '#000000',
    }
};